import { v4 as uuidv4 } from "uuid";

const classes = {
  customSelectContainer: "custom-select_container",
  customSelectContainerActive: "custom-select_container--active",
  customSelect: "custom-select",
  customSelectLabel: "custom-select_label",
  customSelectArrow: "custom-select_arrow",
  customSelectOptions: "custom-select_options",
  customSelectOption: "custom-select_option",
  customSelectOptionActive: "custom-select_option--active",
};

export default class CustomSelect {
  private select: HTMLSelectElement;
  private selectContainer: HTMLDivElement;
  private customSelectContainer: HTMLDivElement;
  private customSelectButton: HTMLButtonElement;
  private customSelectLabel: HTMLSpanElement;
  private customSelectArrow: HTMLSpanElement;
  private customSelectOptions: HTMLUListElement;
  private customSelectOptionElements: HTMLLIElement[] = [];

  constructor(select: HTMLSelectElement, selectContainer: HTMLDivElement) {
    this.select = select;
    this.selectContainer = selectContainer;

    this.createCustomSelectMarkup();
    this.createCustomSelectOptions();
    this.addEventListeners();

    this.selectContainer.appendChild(this.customSelectContainer);
  }

  private createCustomSelectMarkup = () => {
    this.customSelectContainer = document.createElement("div");
    this.customSelectButton = document.createElement("button");
    this.customSelectLabel = document.createElement("span");
    this.customSelectArrow = document.createElement("span");
    this.customSelectOptions = document.createElement("ul");

    this.customSelectContainer.classList.add(classes.customSelectContainer);
    this.customSelectButton.classList.add(classes.customSelect);
    this.customSelectLabel.classList.add(classes.customSelectLabel);
    this.customSelectArrow.classList.add(classes.customSelectArrow);
    this.customSelectOptions.classList.add(classes.customSelectOptions);

    this.customSelectContainer.appendChild(this.customSelectButton);
    this.customSelectButton.appendChild(this.customSelectLabel);
    this.customSelectButton.appendChild(this.customSelectArrow);
    this.customSelectContainer.appendChild(this.customSelectOptions);

    this.customSelectButton.tabIndex = 0;
    this.customSelectButton.role = "combobox";
    this.customSelectButton.setAttribute("aria-haspopup", "listbox");
    this.customSelectOptions.role = "listbox";
    this.customSelectLabel.textContent = this.getSelectedOption().text;
  }

  private createCustomSelectOptions = () => {
    for (let i = 0; i < this.select.options.length; i++) {
      const option = this.select.options[i];
      const id = uuidv4();

      option.dataset.id = id;

      const optionElement = this.createOptionElement(option, id);
      this.customSelectOptions.appendChild(optionElement);

      optionElement.addEventListener("click", () => {
        this.select.selectedIndex = i;
        this.customSelectLabel.textContent = option.text;
        this.deactivateContainer();
        this.customSelectButton?.focus();
        this.select.dispatchEvent(new Event("change"));
      });

      optionElement.addEventListener("keydown", (e) => {
        if (e.code === "Enter" || e.code === "Space" || e.code === "ArrowDown" || e.code === "ArrowUp" || e.code === "Tab" || e.code === "Escape") {
          e.preventDefault();
        }

        if (e.code === "Enter" || e.code === "Space") {
          optionElement.click();
        } else if (e.code === "ArrowDown") {
          const nextOption = optionElement.nextElementSibling as HTMLElement;

          nextOption?.focus();
        } else if (e.code === "ArrowUp") {
          const previousOption = optionElement.previousElementSibling as HTMLElement;

          previousOption?.focus();
        } else if (e.code === "Tab") {
          if (e.shiftKey) {
            this.customSelectButton?.focus();
          } else {
            optionElement.blur();
          }

          this.deactivateContainer();
        } else if (e.code === "Escape") {
          this.customSelectButton?.focus();
          this.deactivateContainer();
        }
      });

      optionElement.addEventListener("focus", (e) => {
        const target = e.target as HTMLElement;

        this.setActiveDescendant(target.id);
        this.customSelectOptionElements.forEach((option) =>
          this.toggleOptionActive(option, option === target)
        );
      })

      this.customSelectOptionElements.push(optionElement);
    }
  }

  private addEventListeners = () => {
    this.customSelectOptions.addEventListener("mouseover", (e) => {
      const target = e.target as HTMLElement;
      const isOption = target.classList.contains(classes.customSelectOption);

      if (isOption) {
        this.setActiveDescendant(target.id);
        this.customSelectOptionElements.forEach((option) =>
          this.toggleOptionActive(option, option === target)
        );
      }
    })

    this.customSelectButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.activateContainer();
    });

    this.customSelectButton.addEventListener("keydown", (e) => {
      if (e.code === "ArrowDown" || e.code === "ArrowUp") {
        e.preventDefault();
        this.activateContainer();
      }
    });

    document.addEventListener("click", (e) => {
      if (!this.customSelectContainer.contains(e.target as Node)) {
        this.deactivateContainer();
      }
    });
  }

  private toggleOptionActive = (optionElement: HTMLLIElement, isActive: boolean) => {
    optionElement.classList.toggle(classes.customSelectOptionActive, isActive);
  }

  private setActiveDescendant = (id: string) => {
    this.customSelectOptions.setAttribute("aria-activedescendant", id);
    this.customSelectButton.setAttribute("aria-activedescendant", id);
  }

  private deactivateContainer = () => {
    this.customSelectContainer.classList.remove(classes.customSelectContainerActive);
    this.customSelectButton.setAttribute("aria-expanded", "false");
  }

  private activateContainer = () => {
    this.customSelectContainer.classList.toggle(classes.customSelectContainerActive);
    this.customSelectButton.setAttribute("aria-expanded", "true");
    const selectedOption = this.getSelectedOption();

    this.setActiveDescendant(selectedOption.dataset.id);

    this.customSelectOptionElements.forEach((option) =>
      this.toggleOptionActive(option, option.id === selectedOption.dataset.id)
    );

    document.getElementById(selectedOption.dataset.id)?.focus();
  }

  private getSelectedOption = () => {
    return this.select.options[this.select.selectedIndex];
  }

  private createOptionElement(option: HTMLOptionElement, id: string) {
    const optionElement = document.createElement("li");

    optionElement.tabIndex = 0;
    optionElement.classList.add(classes.customSelectOption);
    optionElement.textContent = option.text;
    optionElement.dataset.value = option.value;
    optionElement.role = "option";
    optionElement.id = id;

    return optionElement;
  }
}